import React, { useState } from 'react';
import { SharedLink } from './models/shared-link.model';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MoreVertical, Copy, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { useI18n } from '@/hooks/useI18n';
import { useToast } from '@/hooks/useToast';
import { useNavigate } from 'react-router-dom';

interface SharedLinksTableProps {
  links: SharedLink[];
  onEdit: (link: SharedLink) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}

export const SharedLinksTable: React.FC<SharedLinksTableProps> = ({
  links,
  onEdit,
  onDelete,
  isLoading,
}) => {
  const { t } = useI18n();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedLinkId, setSelectedLinkId] = useState<string | null>(null);

  const togglePasswordVisibility = (id: string) => {
    setShowPassword((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const copyToClipboard = (link: SharedLink) => {
    const shareUrl = `${window.location.origin}/#/shared/${link.token}`;
    navigator.clipboard.writeText(shareUrl);
    toast({ variant: 'info',
      title: t('shared.linkCopied'),
      description: shareUrl,
    });
  };

  const handleDeleteClick = (id: string) => {
    setSelectedLinkId(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (selectedLinkId) {
      await onDelete(selectedLinkId);
    }
    setDeleteDialogOpen(false);
    setSelectedLinkId(null);
  };

  const handleItineraryClick = (itineraryId: string) => {
    navigate(`/itinerary-builder?itineraryId=${itineraryId}`);
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const columns: DataTableColumn<SharedLink>[] = [
    {
      id: 'itinerary',
      header: t('shared.itinerary'),
      headClassName: 'w-[18%]',
      cell: (link) => (
        <button
          onClick={() => handleItineraryClick(link.itinerary_id)}
          className="text-blue-600 hover:text-blue-800 hover:underline font-medium text-left break-words"
        >
          {link.itinerary_name}
        </button>
      ),
    },
    {
      id: 'users',
      header: t('shared.users'),
      headClassName: 'w-[16%]',
      cell: (link) => (
        <div className="flex flex-col gap-1">
          {link.client_name ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-1">
                  <Badge variant="outline" className="cursor-help text-xs">
                    {link.client_name}
                  </Badge>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Cliente: {link.client_email}</p>
              </TooltipContent>
            </Tooltip>
          ) : null}
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1">
                <Badge variant="secondary" className="cursor-help text-xs">
                  {link.designer_name}
                </Badge>
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">Advisor: {link.designer_email}</p>
            </TooltipContent>
          </Tooltip>
        </div>
      ),
    },
    {
      id: 'password',
      header: t('shared.password'),
      headClassName: 'w-[10%]',
      cell: (link) =>
        link.password ? (
          <div className="flex items-center gap-2">
            <span className="font-mono text-sm">
              {showPassword[link.id] ? link.password : '••••••••'}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => togglePasswordVisibility(link.id)}
              className="h-6 w-6 p-0"
            >
              {showPassword[link.id] ? (
                <EyeOff className="h-4 w-4" />
              ) : (
                <Eye className="h-4 w-4" />
              )}
            </Button>
          </div>
        ) : (
          <span className="text-muted-foreground italic">-</span>
        ),
    },
    {
      id: 'expiration',
      header: t('shared.expiration'),
      headClassName: 'w-[14%]',
      cell: (link) => (
        <div className="flex flex-col gap-1">
          {link.expires_at ? (
            <>
              <span className="text-sm">
                {formatDate(new Date(link.expires_at))}
              </span>
              <Badge
                variant={link.is_active ? 'default' : 'destructive'}
                className="w-fit text-xs"
              >
                {link.is_active ? t('shared.active') : t('shared.expired')}
              </Badge>
            </>
          ) : (
            <span className="text-muted-foreground italic text-sm">
              {t('shared.noExpiration')}
            </span>
          )}
        </div>
      ),
    },
    {
      id: 'link',
      header: t('shared.link'),
      headClassName: 'w-[32%]',
      cell: (link) => {
        const shareUrl = `${window.location.origin}/#/shared/${link.token}`;
        return (
          <div className="flex items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="font-mono text-xs text-muted-foreground truncate block cursor-help">
                  {shareUrl}
                </span>
              </TooltipTrigger>
              <TooltipContent className="max-w-md">
                <p className="font-mono text-xs break-all">{shareUrl}</p>
              </TooltipContent>
            </Tooltip>
            <Button
              variant="outline"
              size="sm"
              onClick={() => copyToClipboard(link)}
              className="h-7 px-2 shrink-0"
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <TooltipProvider>
      <DataTable<SharedLink>
        data={links}
        columns={columns}
        getRowId={(link) => link.id}
        actionsHeader={t('common.actions')}
        renderActions={(link) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEdit(link)}>
                <Edit className="mr-2 h-4 w-4" />
                {t('common.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => handleDeleteClick(link.id)}
                className="text-red-600"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('common.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        isLoading={isLoading}
        emptyState={t('shared.noLinks')}
      />

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('shared.deleteLink')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('shared.confirmDelete')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
                {t('common.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </TooltipProvider>
  );
};
